/**
 * Side-anchored cart drawer. Opens from the right on desktop and as a
 * full-screen sheet on small viewports. Lines come from the persistent
 * cart store.
 */
import type { CartLine } from '../../store/cartStore';
import { selectCartSubtotalCents, useCartStore } from '../../store/cartStore';
import { computeLineTotal, formatPrice } from '@cracked-mug/shared';
import { Minus, Plus, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

function CartItemRow({ line }: { line: CartLine }) {
  const removeItem = useCartStore((s) => s.removeItem);
  const updateQuantity = useCartStore((s) => s.updateQuantity);

  const customizationSummary = line.selectedCustomizations
    .filter((c) => c.optionLabels.length > 0)
    .map((c) => c.optionLabels.join(', '))
    .join(' · ');

  const lineTotal = computeLineTotal(
    line.basePriceCents,
    line.selectedCustomizations,
    line.quantity,
  );

  return (
    <li className="flex gap-3 py-4 border-b border-wca-gray-light last:border-b-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-medium text-sm leading-tight truncate">{line.itemName}</h3>
          <p className="font-mono text-sm text-wca-red whitespace-nowrap">
            {formatPrice(lineTotal)}
          </p>
        </div>
        {customizationSummary.length > 0 && (
          <p className="text-xs text-wca-gray-mid mt-1 line-clamp-2">{customizationSummary}</p>
        )}
        {line.specialInstructions.length > 0 && (
          <p className="text-xs italic text-wca-gray-mid mt-1 line-clamp-2">
            &ldquo;{line.specialInstructions}&rdquo;
          </p>
        )}
        <div className="flex items-center gap-2 mt-2">
          <button
            type="button"
            onClick={() => updateQuantity(line.cartItemId, line.quantity - 1)}
            disabled={line.quantity <= 1}
            className="rounded-full bg-wca-gray-light w-8 h-8 flex items-center justify-center disabled:opacity-40 hover:bg-wca-gray-mid hover:text-white transition-colors"
            aria-label="Decrease quantity"
          >
            <Minus size={14} />
          </button>
          <span className="font-mono text-sm w-6 text-center tabular-nums">{line.quantity}</span>
          <button
            type="button"
            onClick={() => updateQuantity(line.cartItemId, line.quantity + 1)}
            className="rounded-full bg-wca-gray-light w-8 h-8 flex items-center justify-center hover:bg-wca-gray-mid hover:text-white transition-colors"
            aria-label="Increase quantity"
          >
            <Plus size={14} />
          </button>
          <button
            type="button"
            onClick={() => removeItem(line.cartItemId)}
            className="ml-auto text-wca-gray-mid hover:text-wca-red p-1"
            aria-label="Remove from cart"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </li>
  );
}

export function CartDrawer({ open, onClose }: CartDrawerProps) {
  const items = useCartStore((s) => s.items);
  const subtotal = useCartStore(selectCartSubtotalCents);
  const navigate = useNavigate();

  const handleCheckout = (): void => {
    onClose();
    navigate('/checkout');
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title="Your cart"
      position="right"
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-wca-gray-light">
        <h2 className="font-display text-2xl">Your cart</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close cart"
          className="p-2 text-wca-gray-mid hover:text-wca-black -mr-2"
        >
          <X size={20} />
        </button>
      </div>

      {items.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
          <p className="text-wca-gray-mid">Your cart is empty.</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-3 text-sm text-wca-red hover:underline font-medium"
          >
            ← Back to menu
          </button>
        </div>
      ) : (
        <ul className="flex-1 overflow-y-auto px-6">
          {items.map((line) => (
            <CartItemRow key={line.cartItemId} line={line} />
          ))}
        </ul>
      )}

      {items.length > 0 && (
        <div className="px-6 py-4 border-t border-wca-gray-light space-y-3">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-wca-gray-mid">Subtotal</span>
            <span className="font-mono text-lg tabular-nums">{formatPrice(subtotal)}</span>
          </div>
          <p className="text-xs text-wca-gray-mid">
            Tax and tip added at checkout. Estimated wait shown after you place the order.
          </p>
          <Button variant="primary" size="lg" className="w-full" onClick={handleCheckout}>
            Checkout
          </Button>
        </div>
      )}
    </Modal>
  );
}
